import type { CompetitionDetail } from '@/api/competitionDetail'
import type { CompetitionField, CompetitionRow } from '@/api/competitions'
import { formatInteger, formatPercent, PacingBadge } from '@/design'

import { formatMinor } from '../../list/cells'
import { unavailableReason } from '../../competitions/nullState'
import { formatDayKey, formatDaysTo } from '../format'
import { Tile, TileGroup } from '../pieces'
import { UNDEFINED_FIGURE } from '../reasons'

const count = (value: number) => formatInteger(value)

/**
 * A summary figure's tile props: its value, or — null — the list's one rule for why
 * (`unavailableReason`: missing for a reason, in the list's words) or "—" when undefined.
 */
function summaryFigure(row: CompetitionRow, notAvailable: CompetitionDetail['notAvailable']['summary']) {
  return <V,>(field: CompetitionField, value: V | null, show: (value: V) => string) =>
    value === null ? { reason: unavailableReason(row, field, notAvailable), value: UNDEFINED_FIGURE } : { value: show(value) }
}

/**
 * The competition at a glance: the summary's figures (the competitions list's row, so
 * they agree with the list) and the schedule's — days to its end and draw, its recent
 * velocity and its pacing, each only as the API gives it.
 */
export function SummaryTab({ detail, locale }: { detail: CompetitionDetail; locale: string | undefined }) {
  const { summary: row, schedule, notAvailable, currency } = detail
  const money = (minor: number) => formatMinor(minor, currency, locale)
  const figure = summaryFigure(row, notAvailable.summary)

  return (
    <div className="cd-stack">
      <TileGroup title="Sales in this period">
        <Tile label="Revenue" value={money(row.revenueGrossMinor)} note="Gross, before refunds" />
        <Tile label="Net revenue" {...figure('revenueNetMinor', row.revenueNetMinor, money)} note="After refunds" />
        <Tile label="Orders" value={count(row.orders)} />
        <Tile label="Buyers" value={count(row.buyers)} />
        <Tile label="Average order" {...figure('aovMinor', row.aovMinor, money)} />
        <Tile label="Tickets sold" value={count(row.ticketsSoldInRange)} />
        <Tile label="New customers" value={count(row.newCustomers)} />
        <Tile label="Returning customers" value={count(row.returningCustomers)} />
      </TileGroup>

      <TileGroup title="Lifetime">
        <Tile label="Tickets sold to date" {...figure('ticketsSoldLifetime', row.ticketsSoldLifetime, count)} />
        <Tile label="Capacity" {...figure('totalTicketCapacity', row.totalTicketCapacity, count)} />
        <Tile label="% sold" {...figure('sellThrough', row.sellThrough, (value) => formatPercent(value, 1))} />
      </TileGroup>

      <TileGroup title="Schedule">
        <Tile
          label="Days to end"
          value={schedule.daysToEnd === null ? UNDEFINED_FIGURE : formatDaysTo(schedule.daysToEnd)}
          note={row.endAt ? undefined : 'No end date'}
        />
        <Tile
          label="Days to draw"
          value={schedule.daysToDraw === null ? UNDEFINED_FIGURE : formatDaysTo(schedule.daysToDraw)}
          note={row.drawAt ? undefined : 'No draw date'}
        />
        <VelocityTile detail={detail} locale={locale} />
        <PacingTile detail={detail} />
      </TileGroup>

      <TileGroup title="Measured behaviour in this period" lead={detail.coverage.note}>
        <Tile label="Views" {...figure('views', row.views, count)} />
        <Tile label="Unique viewers" {...figure('uniqueViewers', row.uniqueViewers, count)} />
        <Tile label="Selector opens" {...figure('selectorOpens', row.selectorOpens, count)} />
        <Tile label="Tickets selected" {...figure('ticketsSelected', row.ticketsSelected, count)} />
        <Tile label="Add to baskets" {...figure('addToBaskets', row.addToBaskets, count)} />
        <Tile label="Conversion" {...figure('conversionRate', row.conversionRate, (value) => formatPercent(value))} />
      </TileGroup>
    </div>
  )
}

function VelocityTile({ detail, locale }: { detail: CompetitionDetail; locale: string | undefined }) {
  const { schedule, notAvailable } = detail
  if (schedule.velocityPerDay === null) {
    return <Tile label="Sales velocity" reason={notAvailable.velocityPerDay ?? null} value={UNDEFINED_FIGURE} />
  }
  const over =
    schedule.velocityFrom && schedule.velocityTo
      ? `${formatDayKey(schedule.velocityFrom)} – ${formatDayKey(schedule.velocityTo)} (${schedule.velocityDays} ${schedule.velocityDays === 1 ? 'day' : 'days'})`
      : undefined
  return (
    <Tile
      label="Sales velocity"
      value={`${schedule.velocityPerDay.toLocaleString(locale ?? 'en-GB', { maximumFractionDigits: 2 })} a day`}
      note={over ? `Tickets a day, ${over}` : 'Tickets a day'}
    />
  )
}

function PacingTile({ detail }: { detail: CompetitionDetail }) {
  const { schedule, notAvailable } = detail
  if (schedule.pacing === null) {
    return <Tile label="Pacing" reason={notAvailable.pacing ?? null} value={UNDEFINED_FIGURE} />
  }
  return (
    <Tile
      label="Pacing"
      value={<PacingBadge status={schedule.pacing} />}
      note={
        schedule.pacingExpectedSold === null
          ? 'Against an even spread of sales to sell-out by its end'
          : `${formatInteger(schedule.pacingExpectedSold)} expected sold by now on an even spread to sell-out by its end`
      }
    />
  )
}
